import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import { reportClientError } from './lib/api'
import { ThemeProvider } from './lib/theme'
import { ToastProvider } from './components/ui'
/* Self-hosted rather than from Google Fonts: the donor door is public, and a font
   request would hand every visitor's address to a third party for the sake of type.
   Work Sans throughout. */
import '@fontsource-variable/work-sans'
import './styles.css'

// Failures outside a render — an event handler that threw, a promise nobody
// awaited. The error boundary cannot see these, and until now neither could
// anyone else. Capped inside reportClientError, so a handler failing on every
// keystroke cannot turn into a flood of rows.
window.addEventListener('error', (e) => {
  reportClientError('render', e.error ?? e.message, {
    context: { file: e.filename, line: e.lineno },
  })
})
window.addEventListener('unhandledrejection', (e) => {
  reportClientError('other', e.reason ?? 'Unhandled promise rejection')
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, error) => (error?.status >= 400 && error?.status < 500 ? false : count < 2),
    },
  },
})

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>
)
